/*
 * Live support chat: injects the configured provider's script once, passes the player's
 * identity, and opens the chat. Without a configured widget, openSupport() falls back to email.
 * Each provider's script URL and API calls follow its embed docs (Crisp, Intercom, LiveChat, Tawk.to).
 */
import { SUPPORT_MAILTO } from "@/constants/support";
import type { SupportWidgetConfig } from "@/types";

export interface SupportPlayer {
  userId: string;
  username: string;
  /** Empty for accounts with only a placeholder address. */
  email: string;
  language: string;
  /** Intercom user_hash, signed by the API. */
  userHash?: string;
  /** Crisp user:email signature, signed by the API. */
  emailSignature?: string;
}

/* eslint-disable @typescript-eslint/no-explicit-any -- the providers' globals are untyped */
const w = window as any;

let config: SupportWidgetConfig | null = null;
let language = "en";
let hideLauncher = false;
let loaded = false;
let player: SupportPlayer | null = null;
let tawkReady = false;
let tawkOpenPending = false;

const injectScript = (src: string, attrs: Record<string, string> = {}) => {
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  for (const [key, value] of Object.entries(attrs)) script.setAttribute(key, value);
  document.head.appendChild(script);
};

export const isSupportWidgetEnabled = (): boolean =>
  !!config && config.provider !== "none" && !!config.providerId;

// ---- Crisp ----

const crisp = (...command: unknown[]) => {
  w.$crisp = w.$crisp || [];
  w.$crisp.push(command);
};

const crispIdentify = () => {
  if (!player) {
    crisp("do", "session:reset");
    return;
  }
  if (player.email) {
    crisp(
      "set",
      "user:email",
      player.emailSignature ? [player.email, player.emailSignature] : [player.email],
    );
  }
  crisp("set", "user:nickname", [player.username]);
  crisp("set", "session:data", [[["user_id", player.userId]]]);
};

// ---- Intercom ----

const intercomBoot = () => {
  const settings: Record<string, unknown> = {
    api_base: "https://api-iam.intercom.io",
    app_id: config!.providerId,
    hide_default_launcher: hideLauncher,
    language_override: language,
  };
  if (player) {
    Object.assign(settings, {
      user_id: player.userId,
      name: player.username,
      ...(player.email && { email: player.email }),
      ...(player.userHash && { user_hash: player.userHash }),
    });
  }
  w.Intercom("boot", settings);
};

// ---- LiveChat ----

const liveChat = (...command: unknown[]) => w.LiveChatWidget?.call(...command);

const liveChatIdentify = () => {
  if (!player) {
    liveChat("set_session_variables", {});
    return;
  }
  liveChat("set_customer_name", player.username);
  if (player.email) liveChat("set_customer_email", player.email);
  liveChat("set_session_variables", { user_id: player.userId });
};

// ---- Tawk.to ----

const tawkIdentify = () => {
  if (!tawkReady) return;
  if (!player) {
    w.Tawk_API?.logout?.(() => undefined);
    return;
  }
  w.Tawk_API?.setAttributes?.(
    { name: player.username, ...(player.email && { email: player.email }), id: player.userId },
    () => undefined,
  );
};

const load = () => {
  if (loaded || !isSupportWidgetEnabled()) return;
  loaded = true;
  const id = config!.providerId;

  switch (config!.provider) {
    case "crisp":
      w.$crisp = w.$crisp || [];
      w.CRISP_WEBSITE_ID = id;
      w.CRISP_RUNTIME_CONFIG = { locale: language };
      if (hideLauncher) {
        crisp("do", "chat:hide");
        crisp("on", "chat:closed", () => crisp("do", "chat:hide"));
      }
      crispIdentify();
      injectScript("https://client.crisp.chat/l.js");
      break;
    case "intercom": {
      // Intercom's own queue stub: calls made before the script loads are replayed.
      const queue: any = (...args: unknown[]) => queue.c(args);
      queue.q = [] as unknown[];
      queue.c = (args: unknown) => queue.q.push(args);
      w.Intercom = w.Intercom || queue;
      intercomBoot();
      injectScript(`https://widget.intercom.io/widget/${encodeURIComponent(id)}`);
      break;
    }
    case "livechat": {
      w.__lc = {
        ...(w.__lc || {}),
        license: Number(id),
        integration_name: "manual_channels",
        product_name: "livechat",
      };
      // LiveChat's documented stub: queues calls until tracking.js takes over.
      const widget: any = { _q: [] as unknown[], _h: null, _v: "2.0" };
      const send = (command: unknown[]) =>
        widget._h ? widget._h(...command) : widget._q.push(command);
      Object.assign(widget, {
        on: (...args: unknown[]) => send(["on", args]),
        once: (...args: unknown[]) => send(["once", args]),
        off: (...args: unknown[]) => send(["off", args]),
        get: (...args: unknown[]) => {
          if (!widget._h) throw new Error("[LiveChatWidget] You can't use getters before load.");
          return send(["get", args]);
        },
        call: (...args: unknown[]) => send(["call", args]),
        init: () => undefined,
      });
      w.LiveChatWidget = w.LiveChatWidget || widget;
      if (hideLauncher) {
        liveChat("hide");
        w.LiveChatWidget.on("visibility_changed", (data: { visibility: string }) => {
          if (data?.visibility === "minimized") liveChat("hide");
        });
      }
      liveChatIdentify();
      injectScript("https://cdn.livechatinc.com/tracking.js");
      break;
    }
    case "tawk":
      w.Tawk_API = w.Tawk_API || {};
      w.Tawk_LoadStart = new Date();
      if (player) {
        w.Tawk_API.visitor = {
          name: player.username,
          ...(player.email && { email: player.email }),
        };
      }
      w.Tawk_API.onLoad = () => {
        tawkReady = true;
        if (hideLauncher && !tawkOpenPending) w.Tawk_API.hideWidget?.();
        tawkIdentify();
        if (tawkOpenPending) {
          tawkOpenPending = false;
          w.Tawk_API.showWidget?.();
          w.Tawk_API.maximize?.();
        }
      };
      w.Tawk_API.onChatMinimized = () => {
        if (hideLauncher) w.Tawk_API.hideWidget?.();
      };
      injectScript(`https://embed.tawk.to/${id}`, { charset: "UTF-8", crossorigin: "*" });
      break;
  }
};

/** Takes the API's answer; a change of provider applies on the next page load. */
export const configureSupportWidget = (
  next: SupportWidgetConfig,
  options: { language: string; hideLauncher: boolean },
) => {
  if (loaded) return;
  config = next;
  language = options.language;
  hideLauncher = options.hideLauncher;
};

/** Shows the launcher; called after idle, and only with cookie consent (see components/Others/SupportWidget). */
export const loadSupportWidget = () => load();

/** The signed-in player, or null after logout (the chat session is reset). */
export const setSupportPlayer = (next: SupportPlayer | null) => {
  const changed = JSON.stringify(next) !== JSON.stringify(player);
  player = next;
  if (!loaded || !changed || !config) return;
  switch (config.provider) {
    case "crisp":
      crispIdentify();
      break;
    case "intercom":
      w.Intercom?.("shutdown");
      intercomBoot();
      break;
    case "livechat":
      liveChatIdentify();
      break;
    case "tawk":
      tawkIdentify();
      break;
  }
};

/** Opens the support chat, loading it first; email when no chat is configured. */
export const openSupport = () => {
  if (!isSupportWidgetEnabled()) {
    window.location.href = SUPPORT_MAILTO;
    return;
  }
  load();
  switch (config!.provider) {
    case "crisp":
      crisp("do", "chat:show");
      crisp("do", "chat:open");
      break;
    case "intercom":
      w.Intercom?.("show");
      break;
    case "livechat":
      liveChat("maximize");
      break;
    case "tawk":
      if (tawkReady) {
        w.Tawk_API.showWidget?.();
        w.Tawk_API.maximize?.();
      } else {
        tawkOpenPending = true;
      }
      break;
  }
};
/* eslint-enable @typescript-eslint/no-explicit-any */
